import { useState } from 'react'
import { nextId } from './utils'

export const LIST_IGNORE = '__LIST_IGNORE__'

const wrapFile = (file) => ({
  uid: nextId('upload'),
  name: file.name,
  size: file.size,
  type: file.type,
  status: 'done',
  originFileObj: file,
  percent: 100,
})

/**
 * antd Upload's file lifecycle, kept outside the component so Upload and
 * Upload.Dragger share one implementation.
 */
export const useUploadState = ({
  fileList,
  defaultFileList = [],
  onChange,
  onRemove,
  beforeUpload,
  customRequest,
  multiple,
  maxCount,
}) => {
  const [innerList, setInnerList] = useState(defaultFileList)
  const list = fileList ?? innerList

  const publish = (next) => {
    if (!fileList) setInnerList(next)
    return next
  }

  const addFiles = async (selected) => {
    const incoming = Array.from(selected || [])
    if (incoming.length === 0) return

    const accepted = []
    for (const file of incoming) {
      // eslint-disable-next-line no-await-in-loop -- antd validates files in order
      const verdict = beforeUpload ? await beforeUpload(file, incoming) : true
      if (verdict === LIST_IGNORE) continue
      // `false` means "do not auto-upload"; antd still lists the file.
      accepted.push(wrapFile(verdict instanceof File ? verdict : file))
    }
    if (accepted.length === 0) return

    const merged = multiple ? [...list, ...accepted] : accepted
    const next = publish(maxCount ? merged.slice(-maxCount) : merged)
    onChange?.({ file: accepted[accepted.length - 1], fileList: next })

    if (!customRequest) return
    accepted.forEach((entry) =>
      customRequest({
        file: entry.originFileObj,
        onSuccess: (response) => onChange?.({ file: { ...entry, response }, fileList: next }),
        onError: (error) =>
          onChange?.({ file: { ...entry, status: 'error', error }, fileList: next }),
        onProgress: () => {},
      })
    )
  }

  const removeFile = async (entry) => {
    const verdict = onRemove ? await onRemove(entry) : true
    if (verdict === false) return
    const next = publish(list.filter((item) => item.uid !== entry.uid))
    onChange?.({ file: { ...entry, status: 'removed' }, fileList: next })
  }

  return { list, addFiles, removeFile }
}

export { wrapFile }
